(ns story-builder.component.wrapper
  (:require
   ["react" :as react]
   [goog.events :as gevents]
   [goog.events.KeyCodes :as gkc]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [story-builder.component.global-event :as global-event]
   [story-builder.util.core :as util])
  (:import [goog.events EventType]))

(rf/reg-sub
 :wrapper
 (fn [db _]
   (:wrapper db)))

(declare add-ref-to-first-dom)

(defn process-children
  "Processes a sequence of children, returning a vector of new children and a flag
   indicating whether the ref was attached. Uses reduce instead of loop/recur for safety."
  [children ref]
  (if (empty? children)
    [[] false]
    (reduce
     (fn [[processed attached?] child]
       (if attached?
         [(conj processed child) true]
         (let [[new-child child-attached?] (add-ref-to-first-dom child ref)]
           [(conj processed new-child) child-attached?])))
     [[] false]
     children)))

(defn combine-refs
  "Combines two refs by setting both their .current properties"
  [old-ref new-ref]
  (fn [el]
    (when old-ref (set! (.-current old-ref) el))
    (when new-ref (set! (.-current new-ref) el))))

(defn add-ref-to-first-dom
  "Traverses the hiccup element. If it's a vector with a native dom tag (keyword)
   that doesn't already have a :ref, attach the ref. If it has an existing ref,
   combine them. For fragments (:<>) or function components, process their children.
   Otherwise, return the element unchanged with false."
  [element ref]
  (if (vector? element)
    (let [tag (first element)]
      (cond
        (or (= tag :<>) (fn? tag))
        (let [[new-children attached?] (process-children (rest element) ref)]
          [(into [tag] new-children) attached?])

        (keyword? tag)
        (if (and (>= (count element) 2) (map? (second element)))
          (if-let [existing-ref (:ref (second element))]
            [(into [tag (assoc (second element) :ref (combine-refs existing-ref ref))]
                   (drop 2 element))
             true]
            [(into [tag (assoc (second element) :ref ref)] (drop 2 element))
             true])
          [(into [tag {:ref ref}] (rest element))
           true])

        :else
        [element false]))
    [element false]))

(defn render-child-with-ref
  "Traverses the hiccup structure and attaches the ref on the first native element found."
  [child ref]
  (first (add-ref-to-first-dom child ref)))

(defn click-outside-wrapper
  "This wrapper will invoke a callback when the user clicks outside of it.
   An event target is 'outside' if it is not 'inside'.
   An event target is 'inside' if it is either a descendant of the wrapped dom element
   or if it is a descendant of the wrapped dom element.

   This wrapper can be used on menus or modals."
  [{:keys [inside-selector on-click-outside right-click?]
    :or   {on-click-outside util/no-op}}
   _]
  (let [state          (atom {:props {:inside-selector  inside-selector
                                      :on-click-outside on-click-outside}})
        ref            (react/createRef)
        click-listener (fn [event]
                         (let [{:keys [inside-selector
                                       on-click-outside]}     (:props @state)
                               ^js wrapped-element            (.-current ref)
                               ^js target                     (.-target event)
                               click-is-inside?               (if (some? inside-selector)
                                                                (or (some? (.closest target inside-selector))
                                                                    (some-> wrapped-element (.contains target)))
                                                                (some-> wrapped-element (.contains target)))]
                           (when-not click-is-inside?
                             (on-click-outside event))))]
    (r/create-class
     {:component-did-mount
      (fn [this]
        (let [[_ {:keys [inside-selector on-click-outside]
                  :or   {on-click-outside util/no-op}}] (r/argv this)]
          (swap! state assoc :props {:inside-selector  inside-selector
                                     :on-click-outside on-click-outside})
          (.addEventListener js/document (if right-click? "contextmenu" "mousedown") click-listener)
          (when right-click?
            (.addEventListener js/document "mousedown" click-listener))))
      :component-did-update
      (fn [this _]
        (let [[_ {:keys [inside-selector on-click-outside]
                  :or   {on-click-outside util/no-op}}] (r/argv this)]
          (swap! state assoc :props {:inside-selector  inside-selector
                                     :on-click-outside on-click-outside})))
      :component-will-unmount
      (fn [_]
        (.removeEventListener js/document (if right-click? "contextmenu" "mousedown") click-listener)
        (when right-click?
          (.removeEventListener js/document "mousedown" click-listener)))
      :reagent-render
      (fn [_ child]
        (render-child-with-ref child ref))})))

(defn
  ^{:arglists '([{:keys [event-types event-handler debug-id]} child])}
  listen-global-event-wrapper
  "This wrapper will invoke a callback when an event is thrown on js/document.
   It can be used for listening key events on tabs, menus or modals."
  [{:keys [event-types debug-id]} _]
  (let [state                 (atom {})
        save-props!           (fn [this]
                                (let [[_ {:keys [event-handler]}] (r/argv this)]
                                  (swap! state assoc :props {:event-handler event-handler})))
        global-event-listener (fn [event]
                                (let [{:keys [event-handler]} (:props @state)]
                                  (event-handler event)))]
    (r/create-class
     {:constructor
      (fn [this]
        (save-props! this)
        (global-event/register-event-handler! global-event-listener event-types debug-id))

      :component-did-update
      (fn [this]
        (save-props! this))

      :component-will-unmount
      (fn []
        (global-event/unregister-event-handler! global-event-listener))

      :reagent-render
      (fn [_ child]
        ;; When child is undefined, returns nil
        (or child nil))})))

(defn
  ^{:arglists '([{:keys [closing-triggers inside-selector on-close debug-id right-click?]} child])}
  closable
  "A compact combination of `click-outside-wrapper` and `listen-global-event-wrapper`,
   designed to cover the most common usages."
  [_ _]
  (let [state                (atom {})
        save-props!          (fn [this]
                               (let [[_ {:keys [on-close]
                                         :or   {on-close util/no-op}}] (r/argv this)]
                                 (swap! state assoc :props {:on-close on-close})))
        global-event-handler (fn [event]
                               (when-not (.-defaultPrevented event)
                                 (when (= (.-keyCode event) gkc/ESC)
                                   (let [{:keys [on-close]} (:props @state)]
                                     (on-close event)))))]
    (r/create-class
     {:component-did-mount
      (fn [this]
        (save-props! this))

      :component-did-update
      (fn [this]
        (save-props! this))

      :reagent-render
      (fn [{:keys [closing-triggers inside-selector on-close right-click? debug-id]
            :or   {closing-triggers #{:click-outside :escape-key}
                   inside-selector  nil
                   on-close         util/no-op
                   right-click?     false}}
           child]
        (cond->> child
          (contains? closing-triggers :escape-key)
          (conj [listen-global-event-wrapper {:event-types   [EventType.KEYDOWN]
                                              :event-handler global-event-handler
                                              :debug-id      [:closable debug-id]}])

          (contains? closing-triggers :click-outside)
          (conj [click-outside-wrapper {:inside-selector  inside-selector
                                        :on-click-outside on-close
                                        :right-click?     right-click?}])))})))

;; This function may need more testing.
(defn listen-event-wrapper
  "This wrapper will invoke a callback when an event is thrown on the child element."
  [{:keys [event-handler event-types]} _]
  (let [state (atom nil)
        ref   (react/createRef)
        register-event-handler! (fn [dom-element event-handler event-types]
                                  ;;(prn [:register-event-handler! event-types])
                                  (when (some? dom-element)
                                    (let [listener #(event-handler % dom-element)]
                                      (reset! state {:dom-element dom-element
                                                     :listener-keys (into []
                                                                          (map (fn [event-type]
                                                                                 (gevents/listen dom-element
                                                                                                 event-type
                                                                                                 listener)))
                                                                          (set event-types))}))))
        unregister-event-handler! (fn []
                                    ;;(prn [:unregister-event-handler!])
                                    (doseq [listener-key (:listener-keys @state)]
                                      (gevents/unlistenByKey listener-key)
                                      (reset! state nil)))]
    (r/create-class
     {:component-did-mount
      (fn [_]
        (let [dom-element (.-current ref)]
          (register-event-handler! dom-element event-handler event-types)))

      :component-did-update
      (fn [this old-argv]
        (let [old-dom-element (:dom-element @state)
              new-dom-element (.-current ref)
              [_ {old-event-types   :event-types
                  old-event-handler :event-handler}] old-argv
              [_ {new-event-types   :event-types
                  new-event-handler :event-handler}] (r/argv this)]
          (when-not (= [old-dom-element old-event-handler old-event-types]
                       [new-dom-element new-event-handler new-event-types])
            (unregister-event-handler!)
            (register-event-handler! new-dom-element new-event-handler new-event-types))))

      :component-will-unmount
      (fn [_]
        (unregister-event-handler!))

      :reagent-render
      (fn [_ child]
        (render-child-with-ref child ref))})))

(rf/reg-sub
 :wrapper/mouse-selection?
 :<- [:wrapper]
 (fn [db _]
   (:wrapper/mouse-selection? db)))

(rf/reg-fx
 :wrapper/set-cursor
 (fn [value]
   (let [style-el (js/document.createElement "style")]
     (.setAttribute style-el "id" "nws-cursor-style")
     (set! (.-innerText style-el) (str "* { cursor: " value " !important; }"))
     (js/document.head.appendChild style-el))))

(rf/reg-fx
 :wrapper/unset-cursor
 (fn [_]
   (when-let [style-el (js/document.getElementById "nws-cursor-style")]
     (.remove style-el))))

(rf/reg-event-fx
 :wrapper/set-mouse-selection
 (fn [{:keys [db]} [_ v]]
   (merge
    {:db (assoc-in db [:wrapper :wrapper/mouse-selection?] v)}
    (if v
      {:wrapper/set-cursor "default"}
      {:wrapper/unset-cursor true}))))

(defn mouse-selection [{:keys [select-fn]}]
  (let [state     (atom nil)
        this-atom (atom nil)
        ref       (react/createRef)]
    (letfn [(reset-state []
              (rf/dispatch [:wrapper/set-mouse-selection false])
              (when-let [select-area-el (:select-area-el @state)]
                (.remove select-area-el))
              (reset! state nil))
            (mouse-up [_]
              (.removeEventListener js/window "mousemove" mouse-move)
              (.removeEventListener js/window "mouseup" mouse-up)
              (reset-state))
            (mouse-down [e]
              (when (= (.-buttons e) 1) ; Left button press
                ;; Only register mouse-move listener if the mousedown event is not on top of a selectable element
                (let [selectable-els (->> (:selectable-element-ids (second (r/argv @this-atom)))
                                          (map #(js/document.getElementById %))
                                          (filterv some?))]
                  (when (not-any? #(util/event-over-element? e %) selectable-els)
                    (.addEventListener js/window "mousemove" mouse-move)
                    (.addEventListener js/window "mouseup" mouse-up)
                    (let [select-area-el (.-current ref)]
                      (when (.contains select-area-el (.-target e))
                        (reset! state {:mouse-down-x (.-pageX e)
                                       :mouse-down-y (.-pageY e)})))))))
            (mouse-move [e]
              (when @state
                (let [selectable-els (->> (:selectable-element-ids (second (r/argv @this-atom)))
                                          (map #(js/document.getElementById %))
                                          (filterv some?))]
                  (when-not (:select-area-el @state)
                    (rf/dispatch [:wrapper/set-mouse-selection true])
                    (let [select-area-el (js/document.createElement "div")]
                      (.setAttribute select-area-el "class" "ScenesEditor-selectArea")
                      (js/document.body.appendChild select-area-el)
                      (swap! state assoc :select-area-el select-area-el)))
                  (let [{:keys [select-area-el
                                mouse-down-x
                                mouse-down-y
                                overlapping-el-ids]} @state
                        area-rect              (.getBoundingClientRect (.-current ref))
                        current-x              (.-pageX e)
                        current-y              (.-pageY e)
                        top                    (max (min current-y mouse-down-y) (.-y area-rect))
                        left                   (max (min current-x mouse-down-x) (.-x area-rect))]
                    (set! (.. select-area-el -style -width) (str (js/Math.abs (- mouse-down-x (max current-x (.-x area-rect)))) "px"))
                    (set! (.. select-area-el -style -height) (str (js/Math.abs (- mouse-down-y (max current-y (.-y area-rect)))) "px"))
                    (set! (.. select-area-el -style -top) (str top "px"))
                    (set! (.. select-area-el -style -left) (str left "px"))
                    (let [overlapping-els        (filterv #(util/overlapping-elements? % select-area-el) selectable-els)
                          new-overlapping-el-ids (set (map #(.getAttribute % "id") overlapping-els))]
                      ;; For performance, only call select-fn on the first mouse-move, or when overlapping-el-ids have changed since the last mouse move.
                      (swap! state assoc :overlapping-el-ids new-overlapping-el-ids)
                      (when (not= new-overlapping-el-ids overlapping-el-ids)
                        (select-fn new-overlapping-el-ids)))))))]
      (r/create-class
       {:component-did-mount    (fn [this]
                                  (reset! this-atom this)
                                  (let [[_ {:keys [disabled?]}] (r/argv this)]
                                    (when (not disabled?)
                                      (.addEventListener js/window "mousedown" mouse-down))))
        :component-did-update   (fn [this [_ {old-disabled? :disabled?}]]
                                  (let [[_ {:keys [disabled?]}] (r/argv this)]
                                    (cond
                                      (and disabled? (not old-disabled?))
                                      (do
                                        (.removeEventListener js/window "mousedown" mouse-down)
                                        (.removeEventListener js/window "mousemove" mouse-move))
                                      (and (not disabled?) old-disabled?)
                                      (.addEventListener js/window "mousedown" mouse-down))))
        :component-will-unmount (fn [_]
                                  (.removeEventListener js/window "mousedown" mouse-down))
        :reagent-render         (fn [_ child]
                                  (render-child-with-ref child ref))}))))
